import {
  Button,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Link,
  Text,
  useToast,
  VStack,
  Heading,
} from "@chakra-ui/react"
import {
  Link as RouterLink,
  createFileRoute,
  redirect,
} from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useState } from "react"

import Logo from "/assets/images/denborgin-logo.svg"
import { PublicService, type SaasRegisterRequest } from "../client"
import { isLoggedIn } from "../hooks/useAuth"
import { confirmPasswordRules, emailPattern, passwordRules } from "../utils"

export const Route = createFileRoute("/register-company")({
  component: RegisterCompany,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

interface RegisterForm extends SaasRegisterRequest {
  confirm_password: string
}

function RegisterCompany() {
  const toast = useToast()
  const [isSuccess, setIsSuccess] = useState(false)
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<RegisterForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      company_name: "",
      subdomain: "",
      admin_email: "",
      password: "",
      confirm_password: "",
    },
  })

  const onSubmit: SubmitHandler<RegisterForm> = async (data) => {
    try {
      await PublicService.saasRegister(data)
      setIsSuccess(true)
      toast({
        title: "Rejestracja pomyślna",
        description: "Sprawdź swoją skrzynkę e-mail, aby zweryfikować konto.",
        status: "success",
        duration: 5000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title: "Rejestracja nieudana",
        description: error.body?.error || "Coś poszło nie tak. Spróbuj ponownie.",
        status: "error",
        duration: 5000,
        isClosable: true,
      })
    }
  }

  if (isSuccess) {
    return (
      <Flex flexDir="column" justify="center" align="center" h="100vh" p={4}>
        <Container maxW="md" textAlign="center">
          <Image src={Logo} alt="Denborgin logo" maxW="2xs" mx="auto" mb={8} />
          <Heading size="lg" mb={4}>Sprawdź swoją skrzynkę e-mail</Heading>
          <Text fontSize="lg" mb={8}>
            Wysłaliśmy link weryfikacyjny na adres <strong>{getValues("admin_email")}</strong>. 
            Kliknij w link в wiadomości e-mail, aby aktywować profil swojej firmy.
          </Text>
          <Button as={RouterLink} to="/login" variant="primary">
            Powrót do logowania
          </Button>
        </Container>
      </Flex>
    )
  }

  return (
    <Flex flexDir={{ base: "column", md: "row" }} justify="center" h="100vh">
      <Container
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        py={12}
        maxW="sm"
        alignItems="stretch"
        justifyContent="center"
        gap={4}
        centerContent
      >
        <Image
          src={Logo}
          alt="Denborgin logo"
          height="auto"
          maxW="2xs"
          alignSelf="center"
          mb={4}
        />
        <Heading size="md" textAlign="center" mb={2}>Zarejestruj swoją firmę</Heading>
        <Text textAlign="center" color="gray.500" mb={4}>Rozpocznij darmowy okres próbny HRM już dziś</Text>

        <VStack spacing={4} width="100%">
          <FormControl id="company_name" isInvalid={!!errors.company_name}>
            <FormLabel htmlFor="company_name">Nazwa firmy</FormLabel>
            <Input
              id="company_name"
              {...register("company_name", { required: "Nazwa firmy jest wymagana" })}
              placeholder="Twoja Firma Sp. z o.o."
            />
            {errors.company_name && (
              <FormErrorMessage>{errors.company_name.message}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl id="subdomain" isInvalid={!!errors.subdomain}>
            <FormLabel htmlFor="subdomain">Subdomena</FormLabel>
            <Flex align="center">
              <Input
                id="subdomain"
                {...register("subdomain", { 
                  required: "Subdomena jest wymagana",
                  pattern: {
                    value: /^[a-z0-9-]+$/,
                    message: "Dozwolone są tylko małe litery, cyfry i myślniki"
                  }
                })}
                placeholder="moja-firma"
              />
              <Text ml={2} fontWeight="bold">.dbgone.com</Text>
            </Flex>
            {errors.subdomain && (
              <FormErrorMessage>{errors.subdomain.message}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl id="admin_email" isInvalid={!!errors.admin_email}>
            <FormLabel htmlFor="admin_email">Email administratora</FormLabel>
            <Input
              id="admin_email"
              {...register("admin_email", {
                required: "Email administratora jest wymagany",
                pattern: emailPattern,
              })}
              placeholder="admin@firma.pl"
              type="email"
            />
            {errors.admin_email && (
              <FormErrorMessage>{errors.admin_email.message}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl id="password" isInvalid={!!errors.password}>
            <FormLabel htmlFor="password">Hasło</FormLabel>
            <Input
              id="password"
              {...register("password", passwordRules())}
              placeholder="Hasło"
              type="password"
            />
            {errors.password && (
              <FormErrorMessage>{errors.password.message}</FormErrorMessage>
            )}
          </FormControl>

          <FormControl
            id="confirm_password"
            isInvalid={!!errors.confirm_password}
          >
            <FormLabel htmlFor="confirm_password">Potwierdź hasło</FormLabel>
            <Input
              id="confirm_password"
              {...register("confirm_password", confirmPasswordRules(getValues))}
              placeholder="Powtórz hasło"
              type="password"
            />
            {errors.confirm_password && (
              <FormErrorMessage>
                {errors.confirm_password.message}
              </FormErrorMessage>
            )}
          </FormControl>
        </VStack>

        <Button variant="primary" type="submit" isLoading={isSubmitting} width="100%" mt={4}>
          Zarejestruj firmę
        </Button>
        <Text mt={4}>
          Masz już konto?{" "}
          <Link as={RouterLink} to="/login" color="blue.500">
            Zaloguj się
          </Link>
        </Text>
      </Container>
    </Flex>
  )
}
